import React, { Component, MouseEvent } from "react";
import DataGrid, { Column, Export, FilterPanel, FilterRow, GroupPanel, HeaderFilter, Pager, Paging, Scrolling, Search, SearchPanel } from "devextreme-react/data-grid";
import { Button, DateBox, LoadPanel, Lookup, Popup, ScrollView, SelectBox } from "devextreme-react";
import { DropDownOptions } from "devextreme-react/autocomplete";
import LookupService, { LookupTypeIndexes, LookupTypeItem } from "../../services/LookupService";
import { DateBoxOnValueChangedEvent, onExportingEvent, SelectBoxOnValueChangedEvent } from "../../types/DevExtremeTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import LightModePageTemplate from "../page/Templates/LightModeTemplate";
import WorkerTimeRecordService from "../../services/WorkerTimeRecordService";
import ArtistProfileComponent from "../Artist-Profile/ArtistProfileComponent";
import { confirm } from "devextreme/ui/dialog";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver";

// Keep this reference stable (an inline array is a new object on every render)
const LOOKUP_SEARCH_EXPR = ["value", "id"];

interface WorkerTimeRecordGridProps {
    workerTimeRecordTypeId: string;
    workerTimeRecordStatusId: string;
    isRefresh?: boolean;
    onRefresh?: (refresh: boolean) => void;
    showFilters?: boolean;
}

interface WorkerTimeRecordGridState {
    dataSource: any[];
    loadIndicatorForGrid: boolean;
    popupVisible: boolean;
    selectedBusinessEntityId: string;
    dateFrom: Date | null;
    serviceId: string;
    venueId: string;
    providerId: string;
    clientId: string;
    serviceDataSource: LookupTypeItem[];
    venueDataSource: LookupTypeItem[];
    providerDataSource: LookupTypeItem[];
    clientDataSource: LookupTypeItem[];
    lookupsLoaded: boolean;
}

class WorkerTimeRecordGrid extends Component<WorkerTimeRecordGridProps, WorkerTimeRecordGridState> {
    state: WorkerTimeRecordGridState;
    workerTimeRecordService: WorkerTimeRecordService;
    lookupService: LookupService;

    constructor(props: WorkerTimeRecordGridProps) {
        super(props);
        this.workerTimeRecordService = new WorkerTimeRecordService();
        this.lookupService = new LookupService();
        this.state = {
            dataSource: [],
            loadIndicatorForGrid: false,
            popupVisible: false,
            selectedBusinessEntityId: "",
            dateFrom: null,
            serviceId: "",
            venueId: "",
            providerId: "",
            clientId: "",
            serviceDataSource: [],
            venueDataSource: [],
            providerDataSource: [],
            clientDataSource: [],
            lookupsLoaded: false,
        };
    }

    componentDidMount() {
        this.loadDataSource();
        if (!this.props.showFilters) {
            return;
        }
        Promise.all([
            this.lookupService.getLookupByLookupTypeIndex(LookupTypeIndexes.serviceType),
            this.lookupService.getLookupByLookupTypeIndex(LookupTypeIndexes.venueType),
            this.lookupService.getLookupByLookupTypeIndex(LookupTypeIndexes.providerType),
            this.lookupService.getLookupByLookupTypeIndex(LookupTypeIndexes.clientType),
        ]).then(([service, venue, provider, client]) => {
            this.setState({
                serviceDataSource: service.data?.data || [],
                venueDataSource: venue.data?.data || [],
                providerDataSource: provider.data?.data || [],
                clientDataSource: client.data?.data || [],
                lookupsLoaded: true,
            });
        }).catch(() => this.setState({ lookupsLoaded: true }));
    }

    componentDidUpdate(prevProps: WorkerTimeRecordGridProps) {
        if (
            prevProps.workerTimeRecordTypeId !== this.props.workerTimeRecordTypeId ||
            prevProps.workerTimeRecordStatusId !== this.props.workerTimeRecordStatusId
        ) {
            this.loadDataSource();
        }
    }

    handleDateFromChanged = (e: DateBoxOnValueChangedEvent) => this.setState({ dateFrom: e.value });
    handleServiceChanged = (e: SelectBoxOnValueChangedEvent) => this.setState({ serviceId: String(e.value ?? "") });
    handleVenueChanged = (e: any) => this.setState({ venueId: String(e.value ?? "") });
    handleProviderChanged = (e: any) => this.setState({ providerId: String(e.value ?? "") });
    handleClientChanged = (e: any) => this.setState({ clientId: String(e.value ?? "") });

    applyFilters = () => this.loadDataSource();

    onExporting = (e: onExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Worker Time Records");

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: "application/octet-stream" }), "WorkerTimeRecords.xlsx");
            });
        });
        e.cancel = true;
    };

    renderFilters = () => (
        <div className="row w-100 align-items-center mx-0 mt-3 py-2">
            <div className="col-lg col-md-4 mb-2 mb-lg-0"><label className="font-weight-bold mb-1">Date From</label><DateBox value={this.state.dateFrom || undefined} onValueChanged={this.handleDateFromChanged} displayFormat="dd/MM/yyyy" useMaskBehavior={true} /></div>
            <div className="col-lg col-md-4 mb-2 mb-lg-0"><label className="font-weight-bold mb-1">Service Id</label><SelectBox dataSource={this.state.serviceDataSource} displayExpr="value" valueExpr="id" value={this.state.serviceId} onValueChanged={this.handleServiceChanged} /></div>
            <div className="col-lg col-md-4 mb-2 mb-lg-0"><label className="font-weight-bold mb-1">Venue Id</label><Lookup dataSource={this.state.venueDataSource} displayExpr="value" valueExpr="id" value={this.state.venueId} onValueChanged={this.handleVenueChanged} searchEnabled={true} searchExpr={LOOKUP_SEARCH_EXPR} searchMode="contains"><DropDownOptions showTitle={false} hideOnOutsideClick={true} height="300px" /></Lookup></div>
            <div className="col-lg col-md-4 mb-2 mb-lg-0"><label className="font-weight-bold mb-1">Provider Id</label><Lookup dataSource={this.state.providerDataSource} displayExpr="value" valueExpr="id" value={this.state.providerId} onValueChanged={this.handleProviderChanged} searchEnabled={true} searchExpr={LOOKUP_SEARCH_EXPR} searchMode="contains"><DropDownOptions showTitle={false} hideOnOutsideClick={true} height="300px" /></Lookup></div>
            <div className="col-lg col-md-4 mb-2 mb-lg-0"><label className="font-weight-bold mb-1">Client Id</label><Lookup dataSource={this.state.clientDataSource} displayExpr="value" valueExpr="id" value={this.state.clientId} onValueChanged={this.handleClientChanged} searchEnabled={true} searchExpr={LOOKUP_SEARCH_EXPR} searchMode="contains"><DropDownOptions showTitle={false} hideOnOutsideClick={true} height="300px" /></Lookup></div>
            <div className="w-100" />
            <div className="col-lg col-md-4 my-5"><Button text="Apply" className="btn btn-primary btn--large" width="15%" onClick={this.applyFilters} /></div>
        </div>
    );

    loadDataSource = () => {
        this.setState({ loadIndicatorForGrid: true });
        // const dateFrom = new Date().toISOString().slice(0, 10);

        this.workerTimeRecordService
            .getIncompleteWorkerTimeRecords(
                this.state.dateFrom ? this.state.dateFrom.toISOString().slice(0, 10) : "",
                this.state.serviceId,
                this.state.venueId,
                this.state.providerId,
                this.state.clientId
            )
            .then((response) => {
                let filteredRows = response.data?.data ?? response.data ?? [];
                if (!Array.isArray(filteredRows)) {
                    filteredRows = [];
                }

                if (this.props.workerTimeRecordTypeId) {
                    filteredRows = filteredRows.filter(
                        (row: any) => String(row.workerTimeRecordTypeId) === this.props.workerTimeRecordTypeId
                    );
                }

                if (this.props.workerTimeRecordStatusId) {
                    filteredRows = filteredRows.filter(
                        (row: any) => String(row.workerTimeRecordStatusId) === this.props.workerTimeRecordStatusId
                    );
                }

                this.setState({
                    dataSource: filteredRows,
                    loadIndicatorForGrid: false
                });
            })
            .catch((error) => {
                this.setState({
                    dataSource: [],
                    loadIndicatorForGrid: false
                });
                const message = "Worker Time Record data load failed: " + JSON.stringify(error);
                if (!this.workerTimeRecordService.traceAsErrorToAppInsights(message)) {
                    console.error(message);
                }
            });
    };

    /**
     * Displays provider profile icon column and opens artist details popup.
     */
    profileDisplay = (cellInfo: any) => {
        const businessEntityId: string = cellInfo?.data?.businessEntityId ? String(cellInfo.data.businessEntityId) : "";
        return (
            <div className="d-flex justify-content-between align-items-center">
                <button
                    className={"btn icon-btn"}
                    aria-label="View artist details"
                    onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        this.setState({
                            popupVisible: true,
                            selectedBusinessEntityId: businessEntityId,
                        });
                    }}
                >
                    <FontAwesomeIcon icon={faUser} />
                </button>
            </div>
        );
    };

    /**
     * Handles popup hide confirmation to match existing grid behavior.
     */
    hideProfilePopup = (e: any) => {
        e.cancel = true;
        confirm("<div class='text-center'>Close screen without saving?</div>", "Close").then((hasConfirmed) => {
            if (hasConfirmed) {
                this.setState({ popupVisible: false, selectedBusinessEntityId: "" });
            }
        });
    };

    hideProfileTab = () => {
        this.setState({ popupVisible: false, selectedBusinessEntityId: "" });
    };

    handlePopUpClose = (flag: boolean) => {
        this.setState({
            popupVisible: flag,
            selectedBusinessEntityId: flag ? this.state.selectedBusinessEntityId : "",
        });
    };

    render() {
        return (
            <>
                <LightModePageTemplate>
                    {this.props.showFilters ? this.renderFilters() : null}
                    <DataGrid
                        elementAttr={{ id: "gridContainer" }}
                        dataSource={this.state.dataSource}
                        showBorders={false}
                        showColumnLines={false}
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        columnAutoWidth={true}
                        columnResizingMode={"widget"}
                        hoverStateEnabled={true}
                        noDataText={"No worker time records found for the filters used"}
                        columnMinWidth={120}
                        onExporting={this.onExporting}
                    >
                        <Paging defaultPageSize={10} />
                        <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                        <HeaderFilter visible={true}>
                            <Search enabled={true} />
                        </HeaderFilter>
                        <FilterRow visible={true} applyFilter="auto" />
                        <GroupPanel visible={true} />
                        <FilterPanel visible={true} />
                        <Export enabled={true} allowExportSelectedData={false} />
                        <Scrolling useNative={true} showScrollbar={"always"} />
                        <SearchPanel visible={true} placeholder={"Search"} />

                        <Column
                            caption="PROFILE"
                            allowExporting={false}
                            allowFiltering={false}
                            cellRender={this.profileDisplay}
                            alignment="left"
                        />

                        <Column
                            dataField="id"
                            caption="ID"
                            dataType="number"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="workerTimeRecordTypeId"
                            caption="TYPE ID"
                            dataType="number"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="workerTimeRecordType"
                            caption="TYPE"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="workerTimeRecordStatusId"
                            caption="STATUS ID"
                            dataType="number"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="workerTimeRecordStatus"
                            caption="STATUS"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="clientId"
                            caption="CLIENT ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="venueId"
                            caption="VENUE ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="venueHouseNumber"
                            caption="VENUE HOUSE NUMBER"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="venueName"
                            caption="VENUE NAME"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="contactId"
                            caption="CONTACT ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="contactName"
                            caption="CONTACT NAME"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="contactMobileTelephone"
                            caption="CONTACT NO"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="businessEntityId"
                            caption="PROVIDER ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="businessEntityShortName"
                            caption="PROVIDER"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="serviceTypeLookUpId"
                            caption="SERVICE TYPE ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="serviceTypeLookUp"
                            caption="SERVICE"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="serviceSubTypeLookUpId"
                            caption="SERVICE SUB TYPE ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="serviceSubTypeLookUp"
                            caption="SERVICE SUB TYPE"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="typeLookUpId"
                            caption="TYPE LOOKUP ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="typeLookUp"
                            caption="TYPE LOOKUP"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="relatedId"
                            caption="RELATED ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="startTime"
                            caption="START TIME"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="finishTime"
                            caption="FINISH TIME"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="name"
                            caption="WORKER NAME"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="idValue"
                            caption="WORKER ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="description"
                            caption="WORK DESCRIPTION"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="internalComments"
                            caption="INTERNAL COMMENTS"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="extTimeRecordId"
                            caption="EXT ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="extUserId"
                            caption="EXT USER ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="extUserName"
                            caption="EXT NAME"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="extVenueId"
                            caption="EXT VENUE ID"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="extVenueName"
                            caption="EXT VENUE NAME"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="extService"
                            caption="EXT SERVICE"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="lastUpdatedOn"
                            caption="LAST UPDATED"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                        <Column
                            dataField="lastUpdatedBy"
                            caption="UPDATED BY"
                            dataType="string"
                            allowFiltering={true}
                            alignment="left"
                        />

                    </DataGrid>
                    <LoadPanel shadingColor="rgba(0,0,0,0.4)" visible={this.state.loadIndicatorForGrid} />
                </LightModePageTemplate>
                {this.state.popupVisible ? (
                    <Popup
                        visible={this.state.popupVisible}
                        onHiding={this.hideProfilePopup}
                        dragEnabled={false}
                        hideOnOutsideClick={true}
                        showTitle={true}
                        showCloseButton={true}
                        title="Artist Details"
                        resizeEnabled={true}
                    >
                        <ScrollView width="100%" height="100%">
                            <ArtistProfileComponent
                                mode="view"
                                id={this.state.selectedBusinessEntityId}
                                businessEntityId={this.state.selectedBusinessEntityId}
                                handlePopUpClose={this.handlePopUpClose}
                                handleFeedbackUpdate={() => null}
                                handleAddArtist={() => null}
                                serviceType="Entertainment-Artist"
                            />
                        </ScrollView>
                    </Popup>
                ) : (null)}
            </>
        );
    }
}

export default WorkerTimeRecordGrid;
